import React from 'react'
import  { RadioInputProps } from '@/inputs/radio-input'
import { FieldPath, FieldValues, Control } from 'react-hook-form'
import { InputGroupProps } from './form-input-wrapper'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '../ui/form'
import { cn } from '@/shared/lib/utils'
import { RadioGroup, RadioGroupItem } from '../ui/radio-group'
import { useDirtyFields } from '../../../features/homeowners/context/dirty-fields-context'
import { useReadOnly } from '../../../features/homeowners/context/read-only-context'

/**
 * Props for the FormRadioInput component
 * 
 * @template TFieldValues - The type of the form values object
 * @template TName - The type of the field name (must be a valid path in TFieldValues)
 */
type FormRadioInputProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = RadioInputProps & {
    /** Props to pass to the InputGroup wrapper component */
    inputGroupProps?: InputGroupProps,
    /** The field name in the form (must be a valid path in TFieldValues) */
    name: TName,
    /** React Hook Form control object */
    control: Control<TFieldValues>,
}

/**
 * A form radio input component that integrates with React Hook Form
 * 
 * This component renders a group of radio buttons where users can select one option.
 * It uses the RadioGroup component from the UI library for consistent styling.
 * 
 * @template TFieldValues - The type of the form values object
 * @template TName - The type of the field name (must be a valid path in TFieldValues)
 * 
 * @param props - Component props
 * @param props.inputGroupProps - Props for the InputGroup wrapper
 * @param props.name - The field name in the form
 * @param props.control - React Hook Form control object
 * @param props.options - Array of radio options to display
 * @param props.radioProps - Additional radio input props
 * 
 * @example
 * ```tsx
 * // In your form component
 * const form = useForm<MyFormType>()
 * 
 * <FormRadioInput
 *   name="gender"
 *   control={form.control}
 *   options={[
 *     { label: "Male", value: "male" },
 *     { label: "Female", value: "female" },
 *     { label: "Other", value: "other" }
 *   ]}
 *   inputGroupProps={{
 *     label: "Gender",
 *   }}
 * />
 * ```
 * 
 * @returns JSX.Element - The rendered radio input component
 */
function FormRadioInput<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({ inputGroupProps, name, control, options, ...props }: FormRadioInputProps<TFieldValues, TName>) {
    // Get dirty field state
    const { isFieldDirty } = useDirtyFields()
    const isDirty = isFieldDirty(name as string)
    
    // Check read-only context
    const { isReadOnly } = useReadOnly()

    return (
        <FormField
            control={control}
            name={name}
            render={({ field, fieldState }) => (
                <FormItem className="space-y-1">
                    {/* Render label if provided */}
                    <FormLabel 
                        hideError
                        className={cn(
                            "transition-colors duration-200",
                            isDirty && "text-[#9c27b0]",
                            inputGroupProps?.labelClassName
                        )}
                    >
                        {inputGroupProps?.label}
                    </FormLabel>
                    <FormControl>
                        {/* Radio group container */}
                        <RadioGroup
                            onValueChange={(value) => !isReadOnly && field.onChange(value)}
                            value={field.value || ""}
                            disabled={isReadOnly}
                            className={cn(
                                "flex gap-5 py-1.5 px-1 rounded-full transition-colors duration-200",
                            )}
                            ref={field.ref}
                        >
                            {/* Render each radio option */}
                            {options.map((option, index) => (
                                <FormItem
                                    className={"flex items-center gap-1 space-y-0"}
                                    key={`${option.value}-radio-option-key-${index}`}
                                >
                                    <FormControl>
                                        <RadioGroupItem
                                            value={option.value}
                                        />
                                    </FormControl>
                                    <FormLabel className="font-normal cursor-pointer" hideError>
                                        {option.label}
                                    </FormLabel>
                                </FormItem>
                            ))}
                        </RadioGroup>
                    </FormControl>
                    {/* Display validation error message */}
                    <FormMessage />
                </FormItem>
            )}
        />
    )
}

export default FormRadioInput